import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtEUR, fmtDate } from "@/lib/api";
import { Share2, Loader2, Shield, TrendingUp, Mail, FileDown, Compass, Facebook, Linkedin } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LineChart, Line, CartesianGrid } from "recharts";
import { toast } from "sonner";

export default function Result() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/evaluations/${id}`).then(r => setData(r.data)).catch(e => setErr(e?.response?.data?.detail || "Eroare"));
  }, [id]);

  const save = async () => {
    if (!email) return toast.error("Introdu emailul");
    setSaving(true);
    try {
      await api.post("/evaluations/save", { evaluation_id: id, email });
      toast.success("Salvat! Verifică /istoric cu acest email");
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
    finally { setSaving(false); }
  };

  const printPdf = () => window.print();

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareText = data ? `Am evaluat proprietatea mea pe EvalEstate: ${fmtEUR(data.estimated_value_eur)}` : "";

  const shareOn = (platform) => {
    const u = encodeURIComponent(shareUrl);
    const t = encodeURIComponent(shareText);
    const links = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${u}`,
      whatsapp: `https://wa.me/?text=${t}%20${u}`,
      x: `https://twitter.com/intent/tweet?text=${t}&url=${u}`,
    };
    window.open(links[platform], "_blank", "noopener,noreferrer,width=600,height=600");
  };

  if (err) return <div className="max-w-xl mx-auto p-10 text-center"><p className="text-red-600">{err}</p><Link to="/evaluare" className="underline">Reîncearcă</Link></div>;
  if (!data) return <div className="max-w-xl mx-auto p-10 text-center text-[#52616B]"><Loader2 className="animate-spin inline mr-2" />Se încarcă rezultatul…</div>;

  const inputMap = data.input;
  const barData = [
    { label: "Min piață", v: data.city_avg_per_sqm_eur - 300, c: "#D9D6CC" },
    { label: "Media oraș", v: data.city_avg_per_sqm_eur, c: "#52616B" },
    { label: "Estimare proprietate", v: data.price_per_sqm_eur, c: "#0F6E5B" },
    { label: "Max piață", v: data.city_avg_per_sqm_eur + 300, c: "#D9D6CC" },
  ];

  // Fake 6mo trend
  const trend = [];
  let v = data.estimated_value_eur * 0.92;
  for (const m of ["Sep","Oct","Nov","Dec","Ian","Feb"]) {
    v = v * (1 + (Math.random() * 0.04 - 0.005));
    trend.push({ month: m, price: Math.round(v) });
  }
  trend[trend.length-1].price = data.estimated_value_eur;

  return (
    <main data-testid="result-page" className="bg-[#F6F4EE] min-h-screen text-[#0D1F2D] print:bg-white">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="mono text-[11px] uppercase tracking-[0.22em] text-[#52616B]">§ Raport evaluare · {fmtDate(data.created_at)}</div>
            <h1 className="font-display text-4xl lg:text-6xl font-bold tracking-tight mt-3">
              {inputMap.property_type.charAt(0).toUpperCase() + inputMap.property_type.slice(1)} · {data.factors.city_label}
            </h1>
            <p className="text-[#52616B] mt-2">{inputMap.surface} m² · {inputMap.neighborhood || "zonă nespecificată"} · {inputMap.year_built || "an nespecificat"}</p>
          </div>
          <div className="flex gap-2 print:hidden">
            <button data-testid="btn-print" onClick={printPdf} className="border border-[#0D1F2D] px-4 h-11 rounded-sm text-sm inline-flex items-center gap-2 hover:bg-[#0D1F2D] hover:text-[#F6F4EE] transition-colors">
              <FileDown size={16} /> Salvează PDF
            </button>
          </div>
        </div>

        {/* Bento Grid */}
        <div className="mt-10 grid lg:grid-cols-12 gap-4">
          {/* Primary value */}
          <div className="lg:col-span-7 bg-[#0D1F2D] text-[#F6F4EE] p-10 ee-rise">
            <div className="mono text-[11px] uppercase tracking-[0.22em] opacity-70">Valoare estimată</div>
            <div className="font-display text-6xl lg:text-7xl font-bold tracking-tight mt-3">{fmtEUR(data.estimated_value_eur)}</div>
            <div className="mt-6 mono text-sm opacity-80">
              Interval: <span className="text-[#0F6E5B]">{fmtEUR(data.min_value_eur)}</span> — <span className="text-[#0F6E5B]">{fmtEUR(data.max_value_eur)}</span>
            </div>
            <div className="mt-10 flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm"><Shield size={16} /> Încredere {data.confidence}%</div>
              <div className="h-px flex-1 bg-[#F6F4EE]/20" />
              <div className="mono text-xs">{data.price_per_sqm_eur} €/m²</div>
            </div>
          </div>

          {/* Confidence meter */}
          <div className="lg:col-span-5 bg-white border border-[#DEDCCF] p-8 ee-rise ee-rise-1">
            <div className="mono text-[11px] uppercase tracking-[0.22em] text-[#52616B]">Scor de încredere</div>
            <div className="mt-4 relative h-3 bg-[#F6F4EE] border border-[#DEDCCF]">
              <div className="absolute inset-y-0 left-0 bg-[#0F6E5B]" style={{ width: `${data.confidence}%` }} />
            </div>
            <div className="flex justify-between mono text-[10px] text-[#52616B] mt-2">
              <span>55%</span><span>75%</span><span>95%</span>
            </div>
            <div className="mt-6 space-y-2 text-sm">
              <Row k="Preț bază oraș" v={`${data.factors.base_price_sqm} €/m²`} />
              <Row k="Multiplicator tip" v={`×${data.factors.type_multiplier}`} />
              <Row k="Multiplicator stare" v={`×${data.factors.condition_multiplier}`} />
              <Row k="Multiplicator an" v={`×${data.factors.year_multiplier}`} />
              <Row k="Bonus dotări" v={`+${data.factors.features_bonus_pct}%`} />
            </div>
          </div>

          {/* Chart - comparison */}
          <div className="lg:col-span-7 bg-white border border-[#DEDCCF] p-8 ee-rise ee-rise-2">
            <div className="flex items-center justify-between">
              <div>
                <div className="mono text-[11px] uppercase tracking-[0.22em] text-[#52616B]">Comparație cu piața</div>
                <div className="font-display text-2xl font-bold mt-1">Preț / m² în {data.factors.city_label}</div>
              </div>
              <TrendingUp className="text-[#0F6E5B]" />
            </div>
            <div className="h-56 mt-6">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barData}>
                  <CartesianGrid stroke="#DEDCCF" strokeDasharray="2 4" vertical={false} />
                  <XAxis dataKey="label" stroke="#52616B" style={{ fontSize: 11 }} />
                  <YAxis stroke="#52616B" style={{ fontSize: 11 }} />
                  <Tooltip cursor={{ fill: "#F6F4EE" }} contentStyle={{ background: "#0D1F2D", border: "none", color: "#F6F4EE", fontSize: 12 }} />
                  <Bar dataKey="v" radius={0}>{barData.map((e, i) => <Cell key={i} fill={e.c} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Trend */}
          <div className="lg:col-span-5 bg-[#F6F4EE] border border-[#DEDCCF] p-8 ee-rise ee-rise-3">
            <div className="mono text-[11px] uppercase tracking-[0.22em] text-[#52616B]">Trend 6 luni</div>
            <div className="font-display text-2xl font-bold mt-1">Evoluție estimată</div>
            <div className="h-48 mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trend}>
                  <XAxis dataKey="month" stroke="#52616B" style={{ fontSize: 11 }} />
                  <YAxis hide domain={["dataMin - 2000", "dataMax + 2000"]} />
                  <Tooltip contentStyle={{ background: "#0D1F2D", border: "none", color: "#F6F4EE", fontSize: 12 }} />
                  <Line dataKey="price" stroke="#0F6E5B" strokeWidth={2} dot={{ r: 3, fill: "#0F6E5B" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* AI report */}
          <div className="lg:col-span-12 bg-white border border-[#DEDCCF] p-10 ee-rise ee-rise-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-[#0D1F2D] text-[#F6F4EE] grid place-items-center"><Compass size={14} /></div>
              <div className="mono text-[11px] uppercase tracking-[0.22em] text-[#52616B]">Analiză AI · GPT-6 Sol</div>
            </div>
            <div className="mt-5 text-base leading-relaxed whitespace-pre-line max-w-3xl text-[#0D1F2D]">
              {data.ai_report}
            </div>
          </div>

          {/* Save + Share */}
          <div className="lg:col-span-7 bg-white border border-[#DEDCCF] p-8 ee-rise ee-rise-5 print:hidden">
            <div className="mono text-[11px] uppercase tracking-[0.22em] text-[#52616B]">Salvează evaluarea</div>
            <p className="text-sm text-[#52616B] mt-2">Primești un link privat pe email și acces la istoricul tău.</p>
            <div className="mt-5 flex gap-2">
              <div className="flex-1 flex items-center border-b border-[#0D1F2D] gap-2 pb-1">
                <Mail size={16} className="text-[#52616B]" />
                <input
                  data-testid="save-email"
                  type="email" placeholder="numele.tau@email.ro"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 bg-transparent focus:outline-none text-base font-display py-2"
                />
              </div>
              <button
                data-testid="save-btn"
                onClick={save} disabled={saving}
                className="bg-[#0F6E5B] hover:bg-[#0A5447] text-white px-5 h-11 rounded-sm font-medium disabled:opacity-50"
              >
                {saving ? "..." : "Salvează"}
              </button>
            </div>
          </div>

          <div className="lg:col-span-5 bg-[#0D1F2D] text-[#F6F4EE] p-8 ee-rise ee-rise-5 print:hidden">
            <div className="mono text-[11px] uppercase tracking-[0.22em] opacity-70">Distribuie evaluarea</div>
            <div className="flex items-center gap-3 mt-5">
              <button data-testid="share-fb" onClick={() => shareOn("facebook")} className="w-11 h-11 border border-[#F6F4EE]/30 hover:bg-[#0F6E5B] hover:border-[#0F6E5B] transition-colors grid place-items-center"><Facebook size={16} /></button>
              <button data-testid="share-li" onClick={() => shareOn("linkedin")} className="w-11 h-11 border border-[#F6F4EE]/30 hover:bg-[#0F6E5B] hover:border-[#0F6E5B] transition-colors grid place-items-center"><Linkedin size={16} /></button>
              <button data-testid="share-wa" onClick={() => shareOn("whatsapp")} className="w-11 h-11 border border-[#F6F4EE]/30 hover:bg-[#0F6E5B] hover:border-[#0F6E5B] transition-colors grid place-items-center mono text-xs">WA</button>
              <button data-testid="share-x" onClick={() => shareOn("x")} className="w-11 h-11 border border-[#F6F4EE]/30 hover:bg-[#0F6E5B] hover:border-[#0F6E5B] transition-colors grid place-items-center mono text-xs">X</button>
              <button data-testid="share-copy" onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Link copiat"); }} className="ml-auto text-sm underline hover:text-[#0F6E5B]"><Share2 size={14} className="inline mr-1" /> Copiază link</button>
            </div>
          </div>
        </div>

        {/* Upsell */}
        <div className="mt-10 border border-[#0F6E5B] bg-[#0F6E5B]/5 p-8 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 print:hidden">
          <div>
            <div className="mono text-[11px] uppercase tracking-[0.22em] text-[#0F6E5B]">Vrei mai mult?</div>
            <div className="font-display text-2xl font-bold mt-1">Evaluare profesională ANEVAR pentru acte oficiale</div>
            <p className="text-[#52616B] text-sm mt-1">Un evaluator autorizat vine la fața locului și întocmește raport acceptat bancar.</p>
          </div>
          <Link to="/evaluare-profesionala" data-testid="btn-anevar" className="bg-[#0D1F2D] text-[#F6F4EE] px-6 h-12 rounded-sm font-medium flex items-center hover:bg-black transition-colors">Solicită evaluare oficială</Link>
        </div>
      </div>
    </main>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between border-b border-[#DEDCCF] pb-2">
      <span className="text-[#52616B] text-xs">{k}</span>
      <span className="mono text-xs">{v}</span>
    </div>
  );
}
